/**
 * Environment detection utilities
 * Matches the environment detection logic from the original web apps
 */

import { ADMIN_AUTH_GOOGLE_CLIENT_ID, ADMIN_AUTH_GOOGLE_CLIENT_ID_STAGING, RESIO_WEB_AUTH_GOOGLE_CLIENT_ID, RESIO_WEB_AUTH_GOOGLE_CLIENT_ID_STAGING, RESIO_IOS_AUTH_GOOGLE_CLIENT_ID_STAGING, RESIO_IOS_AUTH_GOOGLE_CLIENT_ID } from '@/constants/shared/auth';
import { Platform } from 'react-native';
import Config from 'react-native-config';

/**
 * Get the hostname for web platforms
 */
const getHostname = (): string => {
  if (Platform.OS === 'web' && typeof globalThis !== 'undefined' && 'window' in globalThis) {
    return (globalThis as any).window.location.hostname;
  }
  return '';
};

/**
 * Check if the app is running in production or staging environment
 * This matches the logic from both resio-web and admin-web
 */
export const isProductionOrStaging = (): boolean => {
  // Allow override for development testing
  if (process.env.REACT_APP_ENABLE_GOOGLE_SIGNIN_DEV === 'true') {
    return true;
  }
  
  if (Platform.OS === 'web') {
    const mode = getEnvironmentMode();
    return mode === 'production' || mode === 'staging';
  } else {
    // For mobile, check __DEV__ flag
    return !__DEV__;
  }
};

/**
 * Get the current environment mode
 * Priority:
 * 1. ENVIRONMENT variable (set during build)
 * 2. Hostname-based detection for web
 * 3. __DEV__ flag for mobile
 */
export const getEnvironmentMode = (): 'development' | 'staging' | 'production' => {
  // First, check if ENVIRONMENT variable is set (from build configuration)
  const envFromConfig = Config.ENVIRONMENT;
  if (envFromConfig) {
    const normalized = envFromConfig.toUpperCase();
    if (normalized === 'PRODUCTION') return 'production';
    if (normalized === 'STAGING' || normalized === 'STAGE') return 'staging';
    if (normalized === 'DEVELOPMENT' || normalized === 'DEV') return 'development';
  }

  // For web, use hostname-based detection
  if (Platform.OS === 'web') {
    const hostname = getHostname();
    
    // Production domains
    if (hostname === 'beta.resio.com' || hostname === 'app.resio.com') {
      return 'production';
    }
    
    // Staging domains
    if (
      hostname === 'alpha.resio.com' ||
      hostname === 'alpha.staging.resio.com' ||
      hostname === 'app.alpha.resio.com' ||
      hostname.includes('staging.resio.com') ||
      hostname.includes('.review.resio.com')
    ) {
      return 'staging';
    }
    
    // Development (localhost, etc.)
    return 'development';
  } else {
    // For mobile, fallback based on __DEV__
    // This should rarely happen if ENVIRONMENT is set correctly via .env files
    // Default to staging instead of production for safety in non-production builds
    return __DEV__ ? 'development' : 'staging';
  }
};

/**
 * Check if running in development mode
 */
export const isDevelopment = (): boolean => {
  return !isProductionOrStaging();
};

/**
 * Get Google OAuth Client ID based on environment and user type
 * Matches the backend configuration (ADMIN_AUTH_GOOGLE_CLIENT_ID vs RESIO_WEB_AUTH_GOOGLE_CLIENT_ID)
 * 
 * @param isAdmin - Whether this is for admin login (default: false)
 * @returns The appropriate Google OAuth Client ID
 */
export const getGoogleClientId = (isAdmin: boolean = false): string => {
  const mode = getEnvironmentMode();
  
  // Select client ID based on environment and user type
  let clientId: string;
  
  if (mode === 'production') {
    clientId = isAdmin
      ? ADMIN_AUTH_GOOGLE_CLIENT_ID  // ADMIN_AUTH_GOOGLE_CLIENT_ID (production)
      : RESIO_WEB_AUTH_GOOGLE_CLIENT_ID;  // RESIO_WEB_AUTH_GOOGLE_CLIENT_ID (production)
  } else {
    // Staging/Development
    clientId = isAdmin
      ? ADMIN_AUTH_GOOGLE_CLIENT_ID_STAGING  // ADMIN_AUTH_GOOGLE_CLIENT_ID (staging)
      : RESIO_WEB_AUTH_GOOGLE_CLIENT_ID_STAGING;  // RESIO_WEB_AUTH_GOOGLE_CLIENT_ID (staging)
  }

  return clientId;
};

/**
 * Get Google iOS Client ID for native iOS Google Sign-In
 * Used for iOS native authentication
 * 
 * Note: We use the same iOS client ID for both resident and admin within the same environment.
 * The differentiation between resident and admin happens via webClientId on the backend.
 * 
 * @param isAdmin - Whether this is for admin login (default: false)
 * @returns The appropriate Google iOS Client ID
 */
export const getGoogleIOSClientId = (isAdmin: boolean = false): string => {
  const mode = getEnvironmentMode();
  
  // Select iOS client ID based on environment
  // Note: Using resident iOS client ID for both resident and admin
  // Admin differentiation happens via webClientId in backend token verification
  const iosClientId = mode === 'production'
    ? RESIO_IOS_AUTH_GOOGLE_CLIENT_ID  // Production iOS client
    : RESIO_IOS_AUTH_GOOGLE_CLIENT_ID_STAGING;  // Staging iOS client

  // Debug logging (only in development or when explicitly enabled)
  if (__DEV__ || process.env.REACT_APP_DEBUG_GOOGLE_OAUTH === 'true') {
    console.log('[Google OAuth] iOS Client ID selection:', {
      mode,
      isAdmin,
      userType: isAdmin ? 'Admin' : 'Resident',
      platform: Platform.OS,
      iosClientId: iosClientId ? `${iosClientId.substring(0, 20)}...` : 'EMPTY/UNDEFINED',
      isProduction: mode === 'production',
      note: 'Using resident iOS client ID for both resident and admin',
    });
  }

  // Validation: throw error if client ID is empty
  if (!iosClientId || iosClientId.trim() === '') {
    throw new Error(`[Google OAuth] iOS Client ID is empty for ${isAdmin ? 'admin' : 'resident'} mode. Environment: ${mode}`);
  }

  return iosClientId;
};